<template>
  <Modal
    :closeBtn="true"
    :closeBtnHidden="loading"
    @close="closeModal"
    :styleOptions="styleOptions"
  >
    <CircleLoader :inline="true" v-if="gettingData || loading"></CircleLoader>
    <div v-else class="confirm-your-loan-details-modal" :class="{ loading }">
      <h1 v-show="!loading">Confirm Your {{ product }} Details</h1>
      <p v-show="!loading">
        Before you sign, please review your {{ product.toLowerCase() }} terms.
      </p>
      <component :is="detailsSectionComponent" v-on="modalEventListeners" v-bind="modalProps" />
      <div class="footer" :class="{ loading }">
        <p class="error" v-show="!loading">* Required Fields</p>
        <p>
          {{ footerText }}
        </p>
        <next-button
          class="esignButton"
          :disabled="loading"
          :loading="loading"
          text="Ready to Sign"
          @click.prevent="confirmChanges"
        />
      </div>
      <div v-if="esignResponseError" class="text-center error">
        We encountered an error generating your {{ product }} documents. Our team is working on
        resolving the situation and will reach out to you within one business day. For additional
        questions please email <a :href="`mailto:${emailAddress}`"> {{ emailAddress }}</a
        >.
      </div>
      <div></div>
    </div>
    <div v-show="loading" class="footer" :class="{ loading }">
      <p>
        {{ footerText }}
      </p>
    </div>
  </Modal>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import Modal from "@/components/shared/Modal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import PelLoanConfigurationDetailsSection from "@/components/pel-loan-configuration/PelLoanConfigurationDetailsSection.vue";
import LocLoanConfigurationDetailsSection from "@/components/loan-configuration/LocLoanConfigurationDetailsSection.vue";
import { ProductIds } from "@/config/constants";
import useEsignConfirmation from "@/composables/useEsignConfirmation";
import useApplicationData from "@/composables/useApplicationData";

const store = useStore();

const emit = defineEmits<{
  (e: "close", payload: boolean): void;
  (e: "openLoanConfigurationModal", payload: boolean): void;
  (e: "openSchoolConfirmationModal", payload: boolean): void;
  (e: "openBorrowAmountModal", payload: boolean): void;
  (e: "openLoanOptionsModal", payload: boolean): void;
}>();

const error = ref<boolean>(false);

const { emailAddress, isPel, productTypeId } = useApplicationData();

const { confirmChanges, esignResponseError, gettingData, loading } = useEsignConfirmation();

const product = computed<string>(() => {
  return productTypeId.value === ProductIds.LOC ? "Line of Credit" : "Loan";
});

const detailsSectionComponent = computed(() => {
  return isPel.value ? PelLoanConfigurationDetailsSection : LocLoanConfigurationDetailsSection;
});

const modalEventListeners = computed(() => {
  switch (detailsSectionComponent.value) {
    case PelLoanConfigurationDetailsSection:
      return {
        openLoanConfigurationModal: openLoanConfigurationModal,
        openSchoolConfirmationModal: openSchoolConfirmationModal,
      };
    case LocLoanConfigurationDetailsSection:
      return {
        openLoanConfigurationModal: openLoanConfigurationModal,
        openSchoolConfirmationModal: openSchoolConfirmationModal,
      };
    default:
      return {};
  }
});

const modalProps = computed(() => {
  switch (detailsSectionComponent.value) {
    default:
      return { loading: loading.value };
  }
});

const footerText = computed<string>(() =>
  loading.value
    ? `Your loan documents are being generated and compiled and will soon be ready to sign. Please do not refresh or close your browser during this process. This message will close automatically when your documents are ready.`
    : "By clicking Ready to Sign, you acknowledge that you have reviewed and agree with all the details listed above.",
);

const stepMinHeight = computed(() => {
  switch (loading.value) {
    case true:
      return "200px";
    default:
      return "300px";
  }
});

const styleOptions = computed(() => {
  return {
    modalBody: {
      minHeight: stepMinHeight.value,
    },
  };
});

function closeModal() {
  store.dispatch("applicationInfoModule/closeModal", "ConfirmYourLoanDetails");
}

function openLoanConfigurationModal() {
  emit("openLoanConfigurationModal", true);
}

function openSchoolConfirmationModal() {
  emit("openSchoolConfirmationModal", true);
}

function openBorrowAmountModal() {
  emit("openBorrowAmountModal", true);
}
async function getLocDetails() {
  const response = await store.dispatch(
    "loanConfigurationModule/getPelDetails",
    store.state.appData?.application?.id,
  );

  if (!response) {
    error.value = true;
  }

  gettingData.value = false;
}

onMounted(async () => {
  await getLocDetails();
});
</script>
<style scoped>
* {
  font-family: var(--header-font);
}

.confirm-your-loan-details-modal {
  padding: 0 28px 16px;
}
.loans-to-refinance-section-wrapper {
  margin-bottom: 1rem;
}
.text-center {
  text-align: center;
}

.confirm-your-loan-details-modal h1 {
  color: #000;
  font-size: 25px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: normal;
}

.confirm-your-loan-details-modal p {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 21px; /* 150% */
}

.confirm-your-loan-details-modal p:first-of-type {
  margin-bottom: 14px;
}

.confirm-your-loan-details-modal .footer p {
  margin-bottom: 11px;
}

.confirm-your-loan-details-modal:is(.loading) {
  text-align: center;
}

.footer:is(.loading) {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px;
}

.footer:is(.loading) p {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  line-height: normal;
}

.esignButton {
  border-radius: 4px;

  display: flex;
  width: 227px;
  height: 34px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  color: #fff;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: 20px; /* 142.857% */
}

.error a {
  color: #8b0e04;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 576px) {
  .esignButton {
    min-width: 100%;
  }
}
</style>
